import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight, BarChart3, Bell, Check, ClipboardList, HeartPulse, Map as MapIcon, MessageSquare, Tag, TrendingUp,
  Users, Wallet, WifiOff,
  type LucideIcon,
} from 'lucide-react';
import {
  BloqueRegistroGanado,
  ExtraEquipo,
  ExtraMapa,
  ExtraPrecios,
  ExtraPrenez,
  ExtraSenasa,
  ExtraSinSenal,
  FaqFuncionalidades,
  TablaFuncionalidades,
} from '../components/landing/BloquesFuncionalidades';
import { useAuth } from '../contexts/AuthContext';
import useSeoMeta from '../hooks/useSeoMeta';
import seoPaginas from '../content/seoPaginas.json';
import PublicNav from '../components/common/PublicNav';
import PublicFooter from '../components/common/PublicFooter';
import { Eyebrow, Surcos } from '../components/landing/brand';
import { ctaPublico } from '../helpers/ctaPublico';

// /funcionalidades — manual de marca v1.0 (estilos en components/landing/brand.css).
// Los id de cada sección son anclas que usa la landing (/funcionalidades#<id>): no cambiarlos.
// Cada punto está verificado contra la app (0 humo). Sin modelos de lectores ni balanzas.

interface Seccion {
  id: string;
  Icon: LucideIcon;
  title: string;
  subtitle: string;
  points: string[];
  cta: { label: string; to: string };
  /** Texto adicional en la columna izquierda, debajo del subtítulo. */
  extra?: React.ReactNode;
  /** Contenido adicional en la columna derecha, después de la lista. */
  despues?: React.ReactNode;
}

const sections: Seccion[] = [
  {
    id: 'gestion-animales',
    Icon: ClipboardList,
    title: 'Gestión de animales (alta, cambios, histórico)',
    subtitle: 'Trazabilidad clara: cada cambio queda registrado, sin perder contexto.',
    points: [
      'Alta y edición de animales por caravana (con validaciones y autocompletado).',
      'Histórico de eventos para auditoría: peso, preñez, ubicación, estado, observaciones.',
      'Operaciones masivas e importación desde Excel o CSV para acelerar cargas iniciales.',
    ],
    cta: { label: 'Ir a Mi Ganado', to: '/animals' },
  },
  {
    id: 'caravana-electronica',
    Icon: Tag,
    title: 'Caravana electrónica 2026: lectura automática (EID/RFID) + peso',
    subtitle:
      'Preparado para el nuevo esquema obligatorio de caravanas electrónicas: menos tipeo, menos errores, más velocidad en el campo.',
    points: [
      'Conectás el lector de caravana y la balanza por Bluetooth en el celular o por USB en la compu.',
      'Lectores que funcionan como teclado: el sistema detecta la lectura rápida y carga la caravana sola.',
      'Compatible con caravanas FDX-B del estándar ISO 11784/85 (15 dígitos).',
      'El peso de la balanza entra solo, en kg.',
      'Lectura masiva: si el lector transmite varios IDs juntos, se toma el último para evitar duplicados.',
    ],
    cta: { label: 'Cargar animal (con EID / balanza)', to: '/animals/new' },
    despues: <ExtraSenasa />,
  },
  {
    id: 'sin-senal',
    Icon: WifiOff,
    title: 'Funciona sin señal: cargás en la manga y se sincroniza sola',
    subtitle:
      'En buena parte de los campos argentinos la señal llega a la casa y se corta en la manga, que es justo donde se generan los datos. Por eso la app no depende de internet para cargar: pesadas, tactos, vacunas y altas quedan guardados en el celular aunque no haya ni una raya de señal.',
    points: [
      'Carga completa sin conexión: animales, pesadas, eventos sanitarios y reproductivos.',
      'Sincronización automática al volver la señal, sin pasos manuales.',
      'Lector de caravana y balanza funcionan igual con o sin internet, porque se conectan al celular por Bluetooth.',
    ],
    cta: { label: 'Cargar un animal', to: '/animals/new' },
    extra: <ExtraSinSenal />,
  },
  {
    id: 'administracion',
    Icon: Wallet,
    title: 'Administración: gastos, inventario y resultados',
    subtitle: 'Mirá números reales, no intuiciones. Ordená tu operación en minutos.',
    points: [
      'Carga unificada de gastos/compras y consumos de inventario.',
      'Stocks e historial de movimientos (compra/consumo/venta).',
      'Libro diario y resultados para entender rentabilidad por período.',
    ],
    cta: { label: 'Abrir Administración', to: '/administracion' },
  },
  {
    id: 'reportes',
    Icon: BarChart3,
    title: 'Reportes y análisis productivo',
    subtitle: 'Decisiones con datos: distribución, productividad y evolución.',
    points: [
      'Reportes por categorías, ubicación, edad y estado.',
      'Análisis de productividad: destete, kg producidos y evolución de peso.',
      'Paneles interactivos: clic en métricas para ver el detalle que explica cada número.',
    ],
    cta: { label: 'Ver Reportes', to: '/reports' },
  },
  {
    id: 'preñez-salud',
    Icon: HeartPulse,
    title: 'Seguimiento reproductivo y sanitario',
    subtitle: 'Control de preñez y salud para anticiparte a pérdidas y mejorar índices.',
    points: [
      'Tasa de preñez, vacías y distribución por etapas.',
      'Indicadores de pérdidas (muertos/perdidos) y tasa de animales activos.',
      'Filtros inteligentes: del indicador al listado de animales en 1 clic.',
    ],
    cta: { label: 'Análisis de Preñez', to: '/reports/pregnancy' },
    despues: <ExtraPrenez />,
  },
  {
    id: 'precios-valuacion',
    Icon: TrendingUp,
    title: 'Precios y valuación: conectá tu rodeo con el mercado',
    subtitle: 'Valuación estimada del stock en base a precios de referencia, actualizados por mes.',
    points: [
      'Precios almacenados por mes y categoría en base a fuentes públicas.',
      'Valuación por cabeza o por kilo según categoría/peso del animal.',
      'Desglose por rangos y categorías para entender qué explica la valuación total.',
    ],
    cta: { label: 'Ver valuación (Administración)', to: '/administracion' },
    despues: <ExtraPrecios />,
  },
  {
    id: 'mapa',
    Icon: MapIcon,
    title: 'Mapa del campo: potreros, aguadas y pasto sobre la foto satelital',
    subtitle:
      'El registro de ganado dice cuántos animales tenés y cómo vienen. El mapa dice dónde están y cuánto aguanta cada cuadro. Dibujás potreros, aguadas, mangas y alambrados sobre la imagen satelital, y cada animal o lote queda ubicado en su potrero.',
    points: [
      'Potreros, aguadas y mangas dibujados sobre imagen satelital.',
      'Verde por cuadro, medido por satélite mes a mes.',
      'Relieve: dónde está el bajo y dónde se junta el agua.',
      'Carga y capacidad: qué parte de su capacidad está usando cada potrero.',
    ],
    cta: { label: 'Abrir el mapa', to: '/mapa' },
    extra: <ExtraMapa />,
  },
  {
    // El id queda por compatibilidad con links viejos; el contenido describe lo que hay hoy.
    id: 'alertas-notificaciones',
    Icon: Bell,
    title: 'Agenda y recordatorios',
    subtitle: 'Lo que no se puede olvidar, anotado y a mano.',
    points: [
      'Recordatorios con fecha, hora y prioridad: vacunas, vencimientos de medicamentos y tareas.',
      'Agenda y notas del campo en el mismo lugar.',
      'Registro de lluvias.',
    ],
    cta: { label: 'Abrir recordatorios', to: '/herramientas?app=recordatorios' },
  },
  {
    id: 'asistente-ia',
    Icon: MessageSquare,
    title: 'Asistente por IA en la Web',
    subtitle: 'Consultá tu rodeo con lenguaje natural, como si le estuvieras preguntando a un conocido.',
    points: [
      'Consultas en lenguaje natural: "¿Cómo están mis vacas preñadas?", "¿Qué tengo que vacunar esta semana?".',
      'Por texto o por audio. Sin señal, la nota de voz queda guardada y se envía al reconectar.',
      'Acceso rápido a información de animales, indicadores y tareas sin navegar menús.',
      'Respuestas basadas en tus datos reales: el asistente conoce tu establecimiento.',
      'Canal de WhatsApp: próximamente.',
    ],
    cta: { label: 'Probar el asistente', to: '/dashboard' },
  },
  {
    id: 'equipo',
    Icon: Users,
    title: 'Todo el equipo ve lo mismo',
    subtitle:
      'En la mayoría de los campos el que carga no es el que decide. El encargado pesa y vacuna, el dueño mira los números desde la ciudad y el asesor revisa la preñez antes del servicio. Con MiCampoDigital los tres trabajan sobre el mismo registro: lo que se carga en la manga aparece en el celular de los demás apenas se sincroniza.',
    points: [
      'Un solo rodeo, con una sola historia por animal.',
      'Cada persona entra con su rol: dueño, administrador, gerente, operador o solo lectura.',
      'Lo que se carga sin señal se suma al registro de todos al sincronizar.',
    ],
    cta: { label: 'Invitar a mi equipo', to: '/admin/usuarios' },
    extra: <ExtraEquipo />,
  },
];

const VALORES = [
  { title: 'Más velocidad', desc: 'Caravana electrónica + balanza: cargá IDs y pesos sin tipear.' },
  { title: 'Más trazabilidad', desc: 'Históricos, reportes y filtros para entender cada número.' },
  { title: 'Más rentabilidad', desc: 'Administración + valuación: control financiero con foco productivo.' },
];

const FuncionalidadesPage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();

  useSeoMeta(seoPaginas.funcionalidades);

  useEffect(() => {
    if (!location.hash) return;

    const id = decodeURIComponent(location.hash.replace('#', ''));
    const el = document.getElementById(id);
    if (!el) return;

    // React Router no siempre hace scroll automático a anchors.
    // Esto asegura que /funcionalidades#section-id posicione correctamente.
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash]);

  return (
    <div className="lp">
      <PublicNav />

      <main>
        <section className="lp-ph">
          <div className="lp-wrap">
            <Eyebrow>Funcionalidades</Eyebrow>
            <h1 style={{ maxWidth: 900 }}>Todo lo que necesitás para gestionar tu establecimiento</h1>
            <p className="lp-lead" style={{ maxWidth: 720 }}>
              Diseñada para el día a día del productor: menos carga manual, más control, más decisiones con datos.
            </p>
            <BloqueRegistroGanado />
            <div className="lp-perks lp-perks--3 lp-perks--light">
              {VALORES.map((v) => (
                <div key={v.title} className="lp-perk">
                  <h3>{v.title}</h3>
                  <p>{v.desc}</p>
                </div>
              ))}
            </div>
            <nav className="lp-pills" aria-label="Ir a una funcionalidad" style={{ marginTop: 36 }}>
              {sections.map((s) => (
                <a key={s.id} className="lp-pill" href={`#${s.id}`}>
                  <s.Icon size={16} aria-hidden="true" style={{ color: 'var(--lp-pastura)' }} />
                  {s.title.split(/[:(]/)[0].trim()}
                </a>
              ))}
            </nav>
          </div>
        </section>

        {sections.map((s, i) => (
          <section key={s.id} id={s.id} className={`lp-sec ${i % 2 === 0 ? 'lp-sec--white' : ''}`} style={{ padding: '72px 0' }}>
            <div className="lp-wrap lp-split" style={{ alignItems: 'start' }}>
              <div>
                <span className="lp-meta">{String(i + 1).padStart(2, '0')}</span>
                <h2 className="lp-h3" style={{ marginTop: 10 }}>{s.title}</h2>
                <p className="lp-body">{s.subtitle}</p>
                {s.extra}
                <Link className="lp-btn lp-btn--primary lp-btn--sm" to={s.cta.to} style={{ marginTop: 24 }}>
                  {s.cta.label} <ArrowRight aria-hidden="true" />
                </Link>
              </div>
              <div>
                <ul className="lp-checks">
                  {s.points.map((p) => (
                    <li key={p}>
                      <Check className="lp-ico" aria-hidden="true" />
                      {p}
                    </li>
                  ))}
                </ul>
                {s.despues}
              </div>
            </div>
          </section>
        ))}

        <TablaFuncionalidades />

        <section className="lp-sec lp-dark">
          <div className="lp-wrap">
            <div className="lp-sec-h" style={{ maxWidth: 720 }}>
              <Eyebrow>Gratis y sin tarjeta</Eyebrow>
              <h2 className="lp-h2">Listo para probarlo en tu campo.</h2>
              <p className="lp-lead">
                Probalo con tu esquema real: caravanas, lotes/corrales, pesadas y seguimiento reproductivo.
              </p>
            </div>
            <div className="lp-ctas">
              <Link className="lp-btn lp-btn--primary" to={ctaPublico(user).to}>
                {user ? 'Ir al panel principal' : ctaPublico(user).label} <ArrowRight aria-hidden="true" />
              </Link>
              <Link className="lp-btn lp-btn--outline" to="/foro">
                Ver foro de productores
              </Link>
            </div>
          </div>
          <Surcos color="blanco" style={{ right: -120, bottom: -260, width: 860 }} />
        </section>

        <FaqFuncionalidades />
      </main>

      <PublicFooter />
    </div>
  );
};

export default FuncionalidadesPage;
